import React, { useState, useEffect, useCallback, useMemo } from "react";
import { use100vh } from "react-div-100vh";

const DEFAULT_LEFT_WIDTH = 60;

const ResizableLayout = ({ children, embedded = false }) => {
  const [leftWidth, setLeftWidth] = useState(() => {
    const stored = localStorage.getItem("image-grid-split-width");
    return stored ? parseFloat(stored) : DEFAULT_LEFT_WIDTH;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startLeftWidth, setStartLeftWidth] = useState(leftWidth);

  useEffect(() => {
    localStorage.setItem("image-grid-split-width", leftWidth.toString());
  }, [leftWidth]);

  const handleDragStart = useCallback(
    (clientX) => {
      setIsDragging(true);
      setStartX(clientX);
      setStartLeftWidth(leftWidth);
    },
    [leftWidth]
  );

  const handleDragMove = useCallback(
    (clientX) => {
      if (!isDragging) return;
      const containerWidth = window.innerWidth;
      const dx = clientX - startX;
      const newLeftWidth = startLeftWidth + (dx / containerWidth) * 98;
      if (newLeftWidth >= 10 && newLeftWidth <= 88) {
        setLeftWidth(newLeftWidth);
      }
    },
    [isDragging, startX, startLeftWidth]
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      handleDragStart(e.clientX);
    },
    [handleDragStart]
  );

  const handleTouchStart = useCallback(
    (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      handleDragStart(touch.clientX);
    },
    [handleDragStart]
  );

  const handleTouchMove = useCallback(
    (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      handleDragMove(touch.clientX);
    },
    [handleDragMove]
  );

  const handleDoubleClick = useCallback(() => {
    setLeftWidth(DEFAULT_LEFT_WIDTH);
  }, []);

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleDragMove(e.clientX);
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleDragEnd);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleDragEnd);
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleDragEnd);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd, handleTouchMove]);

  const rightWidth = 98 - leftWidth;

  // ── Memoize cloned children so they are not recalculated unnecessarily ──
  const leftContent = useMemo(
    () =>
      React.cloneElement(children[0], {
        containerWidth: (window.innerWidth * leftWidth) / 98,
      }),
    [children, leftWidth]
  );

  const rightContent = useMemo(
    () =>
      React.cloneElement(children[1], {
        containerWidth: (window.innerWidth * rightWidth) / 98,
      }),
    [children, rightWidth]
  );

  const height = use100vh();

  return (
    <div
      style={{
        display: "flex",
        width: embedded ? "100%" : "99vw",
        height: embedded ? "100%" : height,
        position: "relative",
      }}
    >
      <div
        style={{
          width: embedded ? `${(leftWidth / 98) * 100}%` : `${leftWidth}vw`,
          overflow: "hidden",
          transition: isDragging ? "none" : "width 0.1s ease-out",
          marginRight: 6,
        }}
      >
        {leftContent}
      </div>
      <div
        style={{
          width: "12px",
          background: isDragging
            ? "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)"
            : "linear-gradient(135deg, #888 0%, #666 100%)",
          cursor: "col-resize",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s ease",
          margin: "0 -4px",
          zIndex: 10,
          touchAction: "none",
          boxShadow: isDragging ? "0 0 12px rgba(33,150,243,0.5)" : "0 0 4px rgba(0,0,0,0.1)",
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onDoubleClick={handleDoubleClick}
      >
        <div
          style={{
            width: "4px",
            height: "80px",
            background: isDragging
              ? "linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.4) 100%)"
              : "linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.2) 100%)",
            position: "absolute",
            transition: "all 0.2s ease",
            borderRadius: "4px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        />
      </div>
      <div
        style={{
          width: embedded ? `${(rightWidth / 98) * 100}%` : `${rightWidth}vw`,
          overflow: "hidden",
          transition: isDragging ? "none" : "width 0.1s ease-out",
        }}
      >
        {rightContent}
      </div>
    </div>
  );
};

export default ResizableLayout;
